import type { ReactNode } from "react";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

import type { RequestError } from "../api/api-connector";

export function networkErrorContent(): ReactNode {
  return (
    <p className="text-sm">
      <Trans>Please check your internet connection and try again.</Trans>
    </p>
  );
}

export function requestErrorContent(error: RequestError): ReactNode {
  const detail = extractDetailMessage(error.data);

  return (
    <>
      <p className="text-sm whitespace-pre-wrap">{detail}</p>
      <DetailsSection error={error} />
    </>
  );
}

export function accessDeniedContent(error: RequestError): ReactNode {
  const detail = extractDetailMessage(error.data);

  return (
    <>
      <p className="text-sm">
        <Trans>You don't have permission to perform this action.</Trans>
      </p>
      <p className="text-sm whitespace-pre-wrap">{detail}</p>
      <DetailsSection error={error} />
    </>
  );
}

export function unexpectedErrorContent(error: unknown): ReactNode {
  const message =
    error instanceof Error ? (
      error.message
    ) : (
      <Trans>
        An unexpected error occurred. Please try again or contact support.
      </Trans>
    );

  return <p className="text-sm">{message}</p>;
}

function extractDetailMessage(data: unknown): string {
  const detail = (data as Record<string, unknown> | null | undefined)?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) =>
        typeof (item as Record<string, unknown>)?.msg === "string"
          ? (item as Record<string, unknown>).msg
          : null,
      )
      .filter((msg): msg is string => msg !== null);

    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  return i18n._(msg`The server returned an error.`);
}

function DetailsSection({ error }: { error: RequestError }): ReactNode {
  const pathname = new URL(error.request.url).pathname;
  const { method } = error.request;
  const { status } = error.response;

  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
        <Trans>Technical details</Trans>
      </summary>
      <div className="mt-2 space-y-2">
        <p className="text-xs text-muted-foreground">
          {method} {pathname} — Status {status}
        </p>
        <pre className="max-h-48 overflow-auto rounded bg-muted p-2 text-xs font-mono whitespace-pre-wrap">
          {JSON.stringify(error.data, null, 2)}
        </pre>
      </div>
    </details>
  );
}
